import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { Callout } from '@/components/Callout/Callout';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { MemberList } from '@/components/MemberList/MemberList';
import { MemberRoleForm } from '@/components/MemberRoleForm/MemberRoleForm';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  BROKERAGE_MEMBERS_QUERY,
  BROKERAGE_ROLES_QUERY,
  DEACTIVATE_MEMBER,
  REACTIVATE_MEMBER,
  SET_MEMBER_ROLE,
  type BrokerageMember,
  type BrokerageMembersResult,
  type BrokerageRolesResult,
  type MemberRole,
} from '@/graphql/brokerages';
import { extraRoles, memberLabel } from '@/lib/brokerages';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { useToast } from '@/toast/useToast';

import styles from '../BrokeragesContainer.module.css';

export interface SectionProps {
  tenantId: string;
  canEdit: boolean;
  onAccessChanged: () => void;
}

/** A brokerage's members and their roles; the main app picks role changes up too. */
export function MembersSection({
  tenantId,
  canEdit,
  canReadRoles,
  meId,
  onAccessChanged,
}: SectionProps & { canReadRoles: boolean; meId: string | null }) {
  const toast = useToast();
  const members = useQuery<BrokerageMembersResult>(BROKERAGE_MEMBERS_QUERY, {
    variables: { tenantId },
    fetchPolicy: 'cache-and-network',
  });
  const roles = useQuery<BrokerageRolesResult>(BROKERAGE_ROLES_QUERY, {
    variables: { tenantId },
    skip: !canReadRoles,
  });
  const refetch = {
    refetchQueries: ['BrokerageMembers', 'BrokerageRoles'],
    awaitRefetchQueries: true,
  };
  const [setMemberRole] = useMutation(SET_MEMBER_ROLE, refetch);
  const [deactivate] = useMutation(DEACTIVATE_MEMBER, refetch);
  const [reactivate] = useMutation(REACTIVATE_MEMBER, refetch);

  const [editing, setEditing] = useState<BrokerageMember | null>(null);
  const [removing, setRemoving] = useState<BrokerageMember | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roleList = roles.data?.roles ?? [];
  const label = (member: BrokerageMember) => memberLabel(member, meId);

  const run = async (call: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      toast.success({ title: success });
      return true;
    } catch (cause) {
      setError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        onAccessChanged();
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const personVariables = (member: BrokerageMember) => ({ tenantId, personId: member.personId });

  const onSaveRole = async (role: MemberRole) => {
    if (
      editing &&
      (await run(
        () => setMemberRole({ variables: { ...personVariables(editing), role } }),
        'Role updated',
      ))
    ) {
      setEditing(null);
    }
  };

  const onRemove = async () => {
    if (
      removing &&
      (await run(
        () => deactivate({ variables: personVariables(removing) }),
        `${label(removing)} removed`,
      ))
    ) {
      setRemoving(null);
    }
  };

  const onRestore = (member: BrokerageMember) =>
    void run(() => reactivate({ variables: personVariables(member) }), `${label(member)} restored`);

  const rows = members.data?.brokerage.members ?? [];

  return (
    <section aria-label="Members" className={styles.section}>
      <p className={styles.sectionLede}>
        Everyone in this brokerage and their main role. Role changes and removals here update the
        main app too.
      </p>
      {error && !editing && !removing ? (
        <Callout tone="danger" title="That didn't work">
          {error}
        </Callout>
      ) : null}
      {members.error && !members.data ? (
        <Callout tone="danger" title="Members couldn't be loaded">
          {friendlyError(members.error)}
        </Callout>
      ) : members.loading && !members.data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading members…
        </p>
      ) : rows.length === 0 ? (
        <EmptyState icon="lock-key" title="Nobody is in this brokerage yet" />
      ) : (
        <MemberList
          canEdit={canEdit}
          meId={meId}
          members={rows}
          onChangeRole={(member) => {
            setError(null);
            setEditing(member);
          }}
          onDeactivate={(member) => {
            setError(null);
            setRemoving(member);
          }}
          onReactivate={onRestore}
          roles={roleList}
        />
      )}

      {editing ? (
        <MemberRoleForm
          busy={busy}
          current={editing.role}
          error={error}
          extraRoleCount={extraRoles(editing, roleList).length}
          member={label(editing)}
          onCancel={() => setEditing(null)}
          onSubmit={onSaveRole}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          busy={busy}
          confirmLabel="Remove from brokerage"
          error={error}
          onCancel={() => setRemoving(null)}
          onConfirm={onRemove}
          title={`Remove ${label(removing)}?`}
        >
          They lose access to this brokerage in the main app and come off every case they were on.
          You can restore them later with the role they had.
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
